import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core'
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  GripVertical, ChevronDown, ChevronRight, Sparkles, Check,
  BookOpen, X, Edit3, RefreshCw, Layers, AlertTriangle, Plus, Trash2,
} from 'lucide-react'
import api from '../../lib/api'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function totalPages(sections) {
  if (!sections) return 0
  return sections.reduce((sum, s) => {
    if (s.type === 'front_matter') return sum + (s.items || []).reduce((a, i) => a + (i.pages || 0), 0)
    if (s.type === 'chapter')     return sum + (s.estimated_pages || 0)
    return sum + (s.estimated_pages || 0)
  }, 0)
}

function chapterList(sections) {
  return (sections || []).filter(s => s.type === 'chapter')
}

// ─── Section type badge ───────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const MAP = {
    front_matter: { label: 'Front',  color: '#4C9BC9' },
    chapter:      { label: 'Ch',     color: '#2563eb' },
    references:   { label: 'Refs',   color: '#3DB87A' },
    appendices:   { label: 'App',    color: '#9B4CC9' },
  }
  const { label, color } = MAP[type] || { label: type, color: '#8B9BB4' }
  return (
    <span
      className="px-1.5 py-0.5 rounded font-mono text-xs shrink-0"
      style={{ background: `${color}18`, color }}
    >
      {label}
    </span>
  )
}

// ─── Inline editable text ─────────────────────────────────────────────────────

function InlineEdit({ value, onChange, className, style, type = 'text', min, max }) {
  const [editing, setEditing] = useState(false)
  const [draft,   setDraft]   = useState(value)
  const inputRef = useRef(null)

  useEffect(() => { setDraft(value) }, [value])

  const commit = () => {
    setEditing(false)
    if (type === 'number') {
      const n = parseInt(draft, 10)
      if (!isNaN(n) && n !== value) onChange(n)
      else setDraft(value)
    } else {
      if (String(draft).trim() && draft !== value) onChange(String(draft).trim())
      else setDraft(value)
    }
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        type={type}
        min={min}
        max={max}
        value={draft}
        onChange={e => setDraft(type === 'number' ? e.target.value : e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false) } }}
        className={`bg-transparent outline-none border-b border-brand-500 ${className}`}
        style={style}
      />
    )
  }

  return (
    <span
      className={`group cursor-text ${className}`}
      style={style}
      onDoubleClick={() => setEditing(true)}
    >
      {value}
      <Edit3
        className="inline ml-1.5 opacity-0 group-hover:opacity-40 transition-opacity"
        style={{ width: 10, height: 10 }}
      />
    </span>
  )
}

// ─── Sortable Chapter Row ─────────────────────────────────────────────────────

function SortableChapter({ section, isSelected, onSelect, onRename, onPagesChange, onDelete }) {
  const {
    attributes, listeners, setNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: section.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const [expanded, setExpanded] = useState(false)

  if (section.type !== 'chapter') {
    return (
      <div
        ref={setNodeRef}
        className="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
        onClick={() => onSelect(section)}
        style={{
          ...style,
          background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
          border: `1px solid ${isSelected ? 'rgba(37,99,235,0.3)' : 'transparent'}`,
        }}
      >
        <button {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing shrink-0">
          <GripVertical style={{ width: 14, height: 14, color: '#e2e8f0' }} />
        </button>
        <TypeBadge type={section.type} />
        <span className="font-sans text-sm text-acdyon-textMuted flex-1 truncate">{section.title}</span>
        <InlineEdit
          value={section.estimated_pages}
          onChange={v => onPagesChange(section.id, v)}
          type="number" min={1} max={999}
          className="font-mono text-xs text-acdyon-textFaint w-8 text-right shrink-0"
          style={{ width: 28 }}
        />
        <span className="font-mono text-xs text-acdyon-textFaint shrink-0">p</span>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
        style={{
          background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
          border: `1px solid ${isSelected ? 'rgba(37,99,235,0.3)' : 'transparent'}`,
        }}
        onClick={() => onSelect(section)}
      >
        <button {...attributes} {...listeners} className="p-0.5 cursor-grab active:cursor-grabbing shrink-0">
          <GripVertical style={{ width: 14, height: 14, color: '#e2e8f0' }} />
        </button>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
          className="shrink-0"
        >
          {expanded
            ? <ChevronDown  style={{ width: 13, height: 13, color: '#8EA4BC' }} />
            : <ChevronRight style={{ width: 13, height: 13, color: '#8EA4BC' }} />}
        </button>
        <span className="font-mono text-xs shrink-0" style={{ color: '#60a5fa', minWidth: 20 }}>
          {section.number || section.number === 0 ? `Ch ${section.number}` : `Ch ${section.id?.replace('chapter_', '')}`}
        </span>
        <InlineEdit
          value={section.title}
          onChange={(v) => onRename(section.id, v)}
          className="flex-1 font-sans text-sm text-acdyon-text truncate"
        />
        <InlineEdit
          value={section.estimated_pages}
          onChange={v => onPagesChange(section.id, v)}
          type="number" min={1} max={999}
          className="font-mono text-xs text-acdyon-textFaint w-8 text-right shrink-0"
          style={{ width: 28 }}
        />
        <span className="font-mono text-xs text-acdyon-textFaint shrink-0">p</span>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onDelete(section.id) }}
          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-red-50"
        >
          <Trash2 style={{ width: 11, height: 11, color: '#F87171' }} />
        </button>
      </div>

      {expanded && section.sections && (
        <div className="ml-10 mt-0.5 space-y-px">
          {section.sections.map(sec => (
            <div key={sec.id} className="group flex items-center gap-2 px-3 py-1.5 rounded-md">
              <span className="font-mono text-xs shrink-0" style={{ color: '#8EA4BC', minWidth: 32 }}>{sec.number}</span>
              <span className="flex-1 font-sans text-xs text-acdyon-textMuted truncate">{sec.title}</span>
              <span className="font-mono text-xs text-acdyon-textFaint shrink-0">{sec.pages}p</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Chapter Detail Panel ─────────────────────────────────────────────────────

function ChapterPanel({ section, onUpdate, onDelete }) {
  if (!section) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-center">
        <BookOpen style={{ width: 32, height: 32, color: '#2A4060', marginBottom: 12 }} />
        <p className="font-sans text-sm text-acdyon-textFaint">Select a chapter to view details</p>
        <p className="font-sans text-xs text-acdyon-textFaint mt-1">Double-click any title or page count to edit</p>
      </div>
    )
  }

  if (section.type !== 'chapter') {
    return (
      <div className="p-5 space-y-5">
        <h3 className="font-display text-xl text-acdyon-text">{section.title}</h3>
        <div className="flex items-center gap-3">
          <TypeBadge type={section.type} />
          <span className="font-sans text-sm text-acdyon-textMuted">{section.estimated_pages} pages</span>
        </div>
        {section.type === 'front_matter' && section.items && (
          <div className="space-y-2">
            <p className="font-sans text-xs text-acdyon-textFaint uppercase tracking-wide">Items</p>
            {section.items.map(item => (
              <div key={item.id} className="flex justify-between items-center py-1.5 border-b" style={{ borderColor: '#DDE4EF' }}>
                <span className="font-sans text-sm text-acdyon-textMuted">{item.title}</span>
                <span className="font-mono text-xs text-acdyon-textFaint">{item.pages}p</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const updateSectionTitle = (secId, newTitle) => {
    const updated = { ...section, sections: (section.sections || []).map(s => s.id === secId ? { ...s, title: newTitle } : s) }
    onUpdate(updated)
  }

  const updateSectionPages = (secId, pages) => {
    const updated = { ...section, sections: (section.sections || []).map(s => s.id === secId ? { ...s, pages } : s) }
    onUpdate(updated)
  }

  const updateSubsectionTitle = (secId, subId, newTitle) => {
    const updated = {
      ...section,
      sections: (section.sections || []).map(s => s.id === secId
        ? { ...s, subsections: (s.subsections || []).map(sub => sub.id === subId ? { ...sub, title: newTitle } : sub) }
        : s
      )
    }
    onUpdate(updated)
  }

  const addSection = () => {
    const idx = (section.sections || []).length + 1
    const newSec = { id: `sec_${Date.now()}`, number: `${section.number}.${idx}`, title: `Section ${idx}`, pages: 5, subsections: [] }
    onUpdate({ ...section, sections: [...(section.sections || []), newSec] })
  }

  const deleteSection = (secId) => {
    onUpdate({ ...section, sections: (section.sections || []).filter(s => s.id !== secId) })
  }

  const addSubsection = (secId) => {
    const sec = (section.sections || []).find(s => s.id === secId)
    if (!sec) return
    const idx = (sec.subsections || []).length + 1
    const newSub = { id: `sub_${Date.now()}`, number: `${sec.number}.${idx}`, title: `Subsection ${idx}`, pages: 2 }
    const updated = { ...section, sections: (section.sections || []).map(s => s.id === secId ? { ...s, subsections: [...(s.subsections || []), newSub] } : s) }
    onUpdate(updated)
  }

  const deleteSubsection = (secId, subId) => {
    const updated = {
      ...section,
      sections: (section.sections || []).map(s => s.id === secId
        ? { ...s, subsections: (s.subsections || []).filter(sub => sub.id !== subId) }
        : s
      )
    }
    onUpdate(updated)
  }

  return (
    <div className="p-5 space-y-6 overflow-y-auto">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-mono text-xs text-acdyon-textFaint mb-1">Chapter {section.number}</p>
          <h3 className="font-display text-xl text-acdyon-text leading-tight">{section.title}</h3>
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1">
              <InlineEdit
                value={section.estimated_pages}
                onChange={v => onUpdate({ ...section, estimated_pages: v })}
                type="number" min={1} max={999}
                className="font-mono text-sm w-10"
                style={{ color: '#2563eb' }}
              />
              <span className="font-mono text-sm" style={{ color: '#2563eb' }}>pages</span>
            </div>
            <span className="font-sans text-xs text-acdyon-textFaint">~{(section.estimated_pages || 0) * 250} words</span>
          </div>
        </div>
        <button
          onClick={() => { if (window.confirm(`Delete "${section.title}"?`)) onDelete(section.id) }}
          className="p-2 rounded-lg hover:bg-red-50 transition-colors shrink-0"
          title="Delete chapter"
        >
          <Trash2 style={{ width: 14, height: 14, color: '#F87171' }} />
        </button>
      </div>

      {(section.ai_notes || section.toc_sections?.ai_notes) && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)' }}>
          <p className="font-sans text-xs font-semibold text-brand-600 mb-2 uppercase tracking-wide">AI Writing Notes</p>
          <p className="font-sans text-xs text-acdyon-textMuted leading-relaxed">
            {section.ai_notes || section.toc_sections?.ai_notes}
          </p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-sans text-xs text-acdyon-textFaint uppercase tracking-wide">Sections</p>
          <button
            onClick={addSection}
            className="flex items-center gap-1 px-2 py-1 rounded-md font-sans text-xs transition-colors hover:bg-brand-50"
            style={{ color: '#2563eb', border: '1px solid #bfdbfe' }}
          >
            <Plus style={{ width: 11, height: 11 }} /> Add Section
          </button>
        </div>
        <div className="space-y-3">
          {(section.sections || []).map(sec => (
            <div key={sec.id} className="group">
              <div className="flex items-center gap-2 py-1">
                <span className="font-mono text-xs shrink-0" style={{ color: '#2563eb' }}>{sec.number}</span>
                <InlineEdit
                  value={sec.title}
                  onChange={v => updateSectionTitle(sec.id, v)}
                  className="flex-1 font-sans text-sm text-acdyon-text"
                />
                <InlineEdit
                  value={sec.pages}
                  onChange={v => updateSectionPages(sec.id, v)}
                  type="number" min={1} max={999}
                  className="font-mono text-xs text-acdyon-textFaint w-6 text-right"
                />
                <span className="font-mono text-xs text-acdyon-textFaint">p</span>
                <button
                  onClick={() => addSubsection(sec.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-brand-50"
                  title="Add subsection"
                >
                  <Plus style={{ width: 10, height: 10, color: '#2563eb' }} />
                </button>
                <button
                  onClick={() => deleteSection(sec.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                  title="Delete section"
                >
                  <Trash2 style={{ width: 10, height: 10, color: '#F87171' }} />
                </button>
              </div>
              {sec.subsections && sec.subsections.length > 0 && (
                <div className="ml-8 space-y-1 mt-1">
                  {sec.subsections.map(sub => (
                    <div key={sub.id} className="group/sub flex items-center gap-2 py-0.5">
                      <span className="font-mono text-xs shrink-0" style={{ color: '#8EA4BC' }}>{sub.number}</span>
                      <InlineEdit
                        value={sub.title}
                        onChange={v => updateSubsectionTitle(sec.id, sub.id, v)}
                        className="flex-1 font-sans text-xs text-acdyon-textMuted"
                      />
                      <span className="font-mono text-xs text-acdyon-textFaint shrink-0">{sub.pages}p</span>
                      <button
                        onClick={() => deleteSubsection(sec.id, sub.id)}
                        className="opacity-0 group-hover/sub:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
                      >
                        <Trash2 style={{ width: 9, height: 9, color: '#F87171' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {(!section.sections || section.sections.length === 0) && (
            <p className="font-sans text-xs text-acdyon-textFaint italic">No sections yet — click "Add Section" above</p>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Finalise Modal ───────────────────────────────────────────────────────────

function FinaliseModal({ open, onClose, onConfirm, loading, validation, thesis }) {
  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(6,11,20,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md mx-4 rounded-2xl p-6"
        style={{ background: '#FFFFFF', border: '1px solid #DDE4EF' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(37,99,235,0.1)' }}>
            <Check style={{ width: 18, height: 18, color: '#2563eb' }} />
          </div>
          <div>
            <h3 className="font-display text-xl text-acdyon-text">Finalise Table of Contents</h3>
            <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">This will create chapters and lock the structure</p>
          </div>
        </div>

        {validation && !validation.valid && (
          <div className="mb-4 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle style={{ width: 14, height: 14, color: '#F87171' }} />
              <span className="font-sans text-xs font-semibold text-red-400">Issues to fix before finalising</span>
            </div>
            {validation.errors.map((e, i) => (
              <p key={i} className="font-sans text-xs text-red-400 ml-5">{e}</p>
            ))}
          </div>
        )}

        {validation?.valid && (
          <div className="mb-5 grid grid-cols-3 gap-3">
            {[
              { label: 'Chapters', value: validation.chapterCount },
              { label: 'Est. Pages', value: validation.estimatedPages },
              { label: 'Est. Words', value: `${((validation.estimatedPages || 0) * 250 / 1000).toFixed(0)}k` },
            ].map(({ label, value }) => (
              <div key={label} className="text-center py-3 rounded-lg" style={{ background: '#F4F7FC' }}>
                <p className="font-mono text-lg font-semibold text-brand-600">{value}</p>
                <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        )}

        <p className="font-sans text-sm text-acdyon-textMuted mb-6 leading-relaxed">
          Finalising creates one chapter entry for each chapter in the ToC.
          You can still edit chapter details later, but adding or removing chapters will require regenerating the ToC.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg font-sans text-sm text-acdyon-textMuted transition-colors hover:text-acdyon-text"
            style={{ border: '1px solid #DDE4EF' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || (validation && !validation.valid)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-sans text-sm font-semibold transition-all disabled:opacity-50"
            style={{ background: '#2563eb', color: '#FFFFFF' }}
          >
            {loading ? (
              <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
            ) : (
              <>
                <Check style={{ width: 14, height: 14 }} />
                Finalise &amp; Start Writing
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ToCBuilderPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [thesis,        setThesis]        = useState(null)
  const [sections,      setSections]      = useState([])
  const [selected,      setSelected]      = useState(null)
  const [loading,       setLoading]       = useState(true)
  const [generating,    setGenerating]    = useState(false)
  const [genProgress,   setGenProgress]   = useState('')
  const [showFinalise,  setShowFinalise]  = useState(false)
  const [finalising,    setFinalising]    = useState(false)
  const [saved,         setSaved]         = useState(false)
  const [dirty,         setDirty]         = useState(false)
  const [error,         setError]         = useState('')
  const [activeId,      setActiveId]      = useState(null)

  const abortRef   = useRef(null)
  const initialRef = useRef(true)

  // ── Load thesis ────────────────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/thesis/${id}`)
        setThesis(data)
        if (data.toc_data?.sections) {
          initialRef.current = true
          setSections(data.toc_data.sections)
          setSelected(data.toc_data.sections.find(s => s.type === 'chapter') || null)
        }
      } catch {
        setError('Could not load thesis. Please try again.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  // ── Track dirty state ──────────────────────────────────────────────────────

  useEffect(() => {
    if (initialRef.current) { initialRef.current = false; return }
    setDirty(true)
  }, [sections])

  // ── DnD sensors ───────────────────────────────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = ({ active }) => setActiveId(active.id)

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null)
    if (!over || active.id === over.id) return
    setSections(prev => {
      const oldIdx = prev.findIndex(s => s.id === active.id)
      const newIdx = prev.findIndex(s => s.id === over.id)
      return arrayMove(prev, oldIdx, newIdx)
    })
  }

  // ── Rename section ─────────────────────────────────────────────────────────

  const renameSection = useCallback((sectionId, newTitle) => {
    setSections(prev => prev.map(s => s.id === sectionId ? { ...s, title: newTitle } : s))
    if (selected?.id === sectionId) setSelected(s => ({ ...s, title: newTitle }))
  }, [selected?.id])

  // ── Delete chapter ────────────────────────────────────────────────────────

  const deleteChapter = useCallback((sectionId) => {
    setSections(prev => prev.filter(s => s.id !== sectionId))
    if (selected?.id === sectionId) setSelected(null)
  }, [selected?.id])

  // ── Update selected section (from ChapterPanel edits) ─────────────────────

  const updateSelectedSection = useCallback((updatedSection) => {
    setSections(prev => prev.map(s => s.id === updatedSection.id ? updatedSection : s))
    setSelected(updatedSection)
  }, [])

  // ── Add chapter ───────────────────────────────────────────────────────────

  const addChapter = useCallback(() => {
    const chaps = sections.filter(s => s.type === 'chapter')
    const num   = chaps.length + 1
    const newCh = {
      id:               `chapter_${Date.now()}`,
      type:             'chapter',
      number:           num,
      title:            `Chapter ${num}`,
      estimated_pages:  25,
      sections:         [],
    }
    setSections(prev => {
      const lastChapterIdx = prev.reduce((acc, s, i) => s.type === 'chapter' ? i : acc, -1)
      const insertAt = lastChapterIdx >= 0 ? lastChapterIdx + 1 : prev.length
      return [...prev.slice(0, insertAt), newCh, ...prev.slice(insertAt)]
    })
    setSelected(newCh)
  }, [sections])

  // ── Save ToC ───────────────────────────────────────────────────────────────

  const saveToC = useCallback(async (secs) => {
    const sectionsToSave = secs || sections
    try {
      await api.put(`/thesis/${id}/toc`, {
        tocData: { ...thesis?.toc_data, sections: sectionsToSave, total_estimated_pages: totalPages(sectionsToSave) },
      })
      setSaved(true)
      setDirty(false)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // silent — user can retry via Generate or Finalise
    }
  }, [id, sections, thesis?.toc_data])

  // ── Generate ToC via SSE ───────────────────────────────────────────────────

  const generateToC = async () => {
    setGenerating(true)
    setGenProgress('Connecting to AI…')
    setError('')

    try {
      const token = (await import('../../lib/supabase')).supabase.auth.getSession
        ? (await (await import('../../lib/supabase')).supabase.auth.getSession()).data.session?.access_token
        : null

      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/thesis/${id}/generate-toc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({}),
        }
      )

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error(err.error || 'Generation failed')
      }

      const reader  = response.body.getReader()
      const decoder = new TextDecoder()
      let   buffer  = ''
      let   finalToc = null

      abortRef.current = reader

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() // keep incomplete line

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const evt = JSON.parse(line.slice(6))
            if (evt.status === 'generating') {
              setGenProgress('Claude is building your ToC structure…')
            } else if (evt.chunk) {
              setGenProgress(p => p.length < 80 ? p + evt.chunk : p)
            } else if (evt.done && evt.tocData) {
              finalToc = evt.tocData
            } else if (evt.error) {
              throw new Error(evt.error)
            }
          } catch (parseErr) {
            if (parseErr?.message && !parseErr.message.includes('JSON')) throw parseErr
          }
        }
      }

      if (finalToc?.sections) {
        initialRef.current = true
        setSections(finalToc.sections)
        setSelected(finalToc.sections.find(s => s.type === 'chapter') || null)
        await saveToC(finalToc.sections)
      }
    } catch (err) {
      setError(err.message || 'ToC generation failed. Please try again.')
    } finally {
      setGenerating(false)
      setGenProgress('')
    }
  }

  // ── Finalise ───────────────────────────────────────────────────────────────

  const toc_data_for_validation = {
    sections,
    total_estimated_pages: totalPages(sections),
  }

  const validation = {
    valid:          chapterList(sections).length >= 5 && totalPages(sections) >= 200,
    errors:         [
      ...(chapterList(sections).length < 5  ? [`Need at least 5 chapters — have ${chapterList(sections).length}`] : []),
      ...(totalPages(sections) < 200        ? [`Need at least 200 pages — estimated ${totalPages(sections)}`] : []),
    ],
    chapterCount:   chapterList(sections).length,
    estimatedPages: totalPages(sections),
  }

  const handleFinalise = async () => {
    setFinalising(true)
    try {
      // Save latest edits first
      await saveToC(sections)
      await api.post(`/thesis/${id}/finalise-toc`)
      navigate(`/app/thesis/${id}/chapters`)
    } catch (err) {
      setError(err.response?.data?.error || 'Finalise failed — please try again.')
      setShowFinalise(false)
    } finally {
      setFinalising(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (error && !sections.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertTriangle style={{ width: 32, height: 32, color: '#F87171', marginBottom: 12 }} />
        <p className="font-sans text-sm text-red-400">{error}</p>
      </div>
    )
  }

  const activeSection = sections.find(s => s.id === activeId)
  const pages         = totalPages(sections)
  const chapters      = chapterList(sections)
  const hasToc        = sections.length > 0

  return (
    <>
      <FinaliseModal
        open={showFinalise}
        onClose={() => setShowFinalise(false)}
        onConfirm={handleFinalise}
        loading={finalising}
        validation={validation}
        thesis={thesis}
      />

      <div className="flex flex-col h-full" style={{ maxHeight: 'calc(100vh - 4rem)' }}>

        {/* ── Top action bar ───────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-6 py-3 shrink-0"
          style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF' }}
        >
          <div className="min-w-0">
            <h1 className="font-display text-acdyon-text truncate" style={{ fontSize: '1.2rem', lineHeight: 1.3 }}>
              {thesis?.title || 'Table of Contents'}
            </h1>
            {thesis && (
              <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">
                {thesis.university} · {thesis.programme} · {thesis.methodology}
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {saved && (
              <span className="flex items-center gap-1 font-sans text-xs" style={{ color: '#3DB87A' }}>
                <Check style={{ width: 12, height: 12 }} /> Saved
              </span>
            )}
            {dirty && !saved && (
              <span className="font-sans text-xs" style={{ color: '#F59E0B' }}>Unsaved changes</span>
            )}
            {error && (
              <span className="font-sans text-xs text-red-400 max-w-xs truncate">{error}</span>
            )}

            {/* Generate / Regenerate */}
            <button
              onClick={generateToC}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-medium transition-all disabled:opacity-60"
              style={{ background: hasToc ? '#F4F7FC' : 'rgba(37,99,235,0.1)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.3)' }}
            >
              {generating ? (
                <>
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  {hasToc ? <RefreshCw style={{ width: 14, height: 14 }} /> : <Sparkles style={{ width: 14, height: 14 }} />}
                  {hasToc ? 'Regenerate' : 'Generate ToC with AI'}
                </>
              )}
            </button>

            {/* Finalise */}
            {hasToc && (
              <button
                onClick={() => { saveToC(sections); setShowFinalise(true) }}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-sm font-semibold transition-all disabled:opacity-60"
                style={{ background: '#2563eb', color: '#FFFFFF' }}
              >
                <Check style={{ width: 14, height: 14 }} />
                Finalise ToC
              </button>
            )}
          </div>
        </div>

        {/* ── Generation status bar ─────────────────────────────────── */}
        {generating && (
          <div
            className="px-6 py-2 shrink-0 flex items-center gap-3"
            style={{ background: 'rgba(37,99,235,0.05)', borderBottom: '1px solid rgba(37,99,235,0.1)' }}
          >
            <div className="w-3 h-3 rounded-full border-2 border-brand-500 border-t-transparent animate-spin shrink-0" />
            <p className="font-mono text-xs text-acdyon-textMuted truncate">{genProgress || 'Generating…'}</p>
          </div>
        )}

        {/* ── Main two-column layout ────────────────────────────────── */}
        <div className="flex flex-1 min-h-0">

          {/* Left — ToC tree */}
          <div
            className="flex flex-col border-r"
            style={{ width: 360, background: '#FFFFFF', borderColor: '#DDE4EF', flexShrink: 0 }}
          >
            {/* Stats strip */}
            {hasToc && (
              <div
                className="flex items-center gap-4 px-4 py-2.5 shrink-0"
                style={{ borderBottom: '1px solid #DDE4EF' }}
              >
                <div className="flex items-center gap-1.5">
                  <Layers style={{ width: 12, height: 12, color: '#8EA4BC' }} />
                  <span className="font-mono text-xs" style={{ color: chapters.length >= 5 ? '#3DB87A' : '#F87171' }}>
                    {chapters.length} chapters
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <BookOpen style={{ width: 12, height: 12, color: '#8EA4BC' }} />
                  <span className="font-mono text-xs" style={{ color: pages >= 200 ? '#3DB87A' : '#F87171' }}>
                    ~{pages} pages
                  </span>
                </div>
                {pages < 200 && (
                  <span className="font-sans text-xs" style={{ color: '#F87171' }}>
                    Need {200 - pages} more pages
                  </span>
                )}
              </div>
            )}

            {/* DnD sortable list */}
            <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
              {!hasToc && !generating ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                  <Sparkles style={{ width: 32, height: 32, color: '#DDE4EF', marginBottom: 12 }} />
                  <p className="font-sans text-sm text-acdyon-textFaint">No ToC yet</p>
                  <p className="font-sans text-xs text-acdyon-textFaint mt-1">
                    Click "Generate ToC with AI" to create a complete chapter structure for your thesis
                  </p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    {sections.map(section => (
                      <SortableChapter
                        key={section.id}
                        section={section}
                        isSelected={selected?.id === section.id}
                        onSelect={setSelected}
                        onRename={renameSection}
                        onPagesChange={(sId, pg) => setSections(prev =>
                          prev.map(s => s.id === sId ? { ...s, estimated_pages: pg } : s)
                        )}
                        onDelete={deleteChapter}
                      />
                    ))}
                  </SortableContext>
                  <DragOverlay>
                    {activeSection ? (
                      <div
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg"
                        style={{ background: '#F0F4FA', border: '1px solid rgba(37,99,235,0.3)', opacity: 0.9 }}
                      >
                        <GripVertical style={{ width: 14, height: 14, color: '#2563eb' }} />
                        <TypeBadge type={activeSection.type} />
                        <span className="font-sans text-sm text-acdyon-text truncate">{activeSection.title}</span>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              )}
            </div>

            {/* Add Chapter + Save */}
            {hasToc && (
              <div className="px-3 py-3 shrink-0 space-y-2" style={{ borderTop: '1px solid #DDE4EF' }}>
                <button
                  onClick={addChapter}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-sans text-sm transition-colors"
                  style={{ background: 'rgba(37,99,235,0.06)', color: '#2563eb', border: '1px dashed #bfdbfe' }}
                >
                  <Plus style={{ width: 13, height: 13 }} /> Add Chapter
                </button>
                <button
                  onClick={() => saveToC(sections)}
                  className="w-full py-2 rounded-lg font-sans text-sm transition-colors"
                  style={{
                    background: dirty ? '#2563eb' : 'transparent',
                    color: dirty ? '#ffffff' : '#94a3b8',
                    border: `1px solid ${dirty ? '#2563eb' : '#DDE4EF'}`,
                  }}
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>

          {/* Right — Chapter detail + page calculator */}
          <div className="flex flex-col flex-1 min-w-0" style={{ background: '#F4F7FC' }}>
            {/* Chapter detail */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ChapterPanel section={selected} onUpdate={updateSelectedSection} onDelete={deleteChapter} />
            </div>

            {/* Page summary footer */}
            {hasToc && (
              <div
                className="shrink-0 px-5 py-4"
                style={{ borderTop: '1px solid #DDE4EF', background: '#FFFFFF' }}
              >
                <p className="font-sans text-xs text-acdyon-textFaint uppercase tracking-wide mb-3">
                  Thesis Stats
                </p>
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { label: 'Total Chapters', value: chapters.length },
                    { label: 'Est. Pages',     value: pages },
                    { label: 'Est. Words',     value: `${((pages * 250) / 1000).toFixed(0)}k` },
                    { label: '% to 200 pages', value: `${Math.min(Math.round((pages / 200) * 100), 100)}%` },
                  ].map(({ label, value }) => (
                    <div key={label} className="text-center">
                      <p
                        className="font-mono text-lg font-semibold"
                        style={{ color: pages >= 200 ? '#3DB87A' : '#2563eb' }}
                      >
                        {value}
                      </p>
                      <p className="font-sans text-xs text-acdyon-textFaint mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>
                <div
                  className="mt-3 h-1.5 rounded-full overflow-hidden"
                  style={{ background: '#1E3A5F' }}
                >
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${Math.min((pages / 200) * 100, 100)}%`,
                      background: pages >= 200 ? '#3DB87A' : '#2563eb',
                    }}
                  />
                </div>
                <p className="font-sans text-xs text-acdyon-textFaint mt-1.5 text-right">
                  {pages >= 200 ? 'Meets minimum requirement' : `${200 - pages} more pages needed`}
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  )
}
